// Turn the backend's Plan into a confirmable, VALIDATED plan proposal. The planner captures the
// decomposition; HERE we validate every step against what this user/mount can actually execute — a step
// targeting a page the user can't reach (permission-scoped navTargets / manifest) or an unknown capability
// downgrades the WHOLE plan to a clarification, never a partial run. Per the locked UX rule, a plan is
// ALWAYS confirmed explicitly (one Run for the whole plan) — no auto-execution, ever.
import { buildPageManifest, pageActionLabel } from "./pageManifest";

const KNOWN_CAPABILITIES = new Set(["filters", "charts", "table_view", "navigate", "dashboard_reset"]);
export const MAX_PLAN_STEPS = 5;

// The ROUTE a step's capability pins when its sub_utterance replays through /interpret. Sent as
// `route_hint` so the backend classifier is BYPASSED for plan steps: the capability was decided (and
// user-confirmed) when the plan was built — re-deriving it from the step's words let the router change
// its mind mid-plan ("filter to Florida and export the table" re-read as a brand-new multi_task plan).
// `navigate` is absent on purpose: the plan loop executes navigation deterministically, no interpret call.
export const ROUTE_HINT_BY_CAPABILITY = {
    filters: "filter_query",
    table_view: "table_command",
    charts: "chart_command",
    dashboard_reset: "dashboard_reset",
    platform_qa: "platform_qa"
};

// Human line for one step, shown on the plan card and as the step's progress line.
export const describePlanStep = (step, pageLabels = {}) => {
    const sub = (step.sub_utterance || "").trim();
    switch (step.capability) {
        case "navigate":
            return pageActionLabel("Open", { key: step.target_page, label: pageLabels[step.target_page] || step.target_page });
        case "filters":
            return `Filter: ${sub.replace(/^filter (to|by|for)\s+/i, "")}`;
        case "table_view":
            return `Table: ${sub}`;
        case "charts":
            return `Chart: ${sub}`;
        // Named, because there are now TWO dashboards to reset: in a redirect CHOICE both buttons carry a
        // reset step, and two steps both labelled "Reset the dashboard" would make the pick meaningless.
        // Falls back to the generic wording when the caller supplies no label for the target page.
        case "dashboard_reset":
            return pageLabels[step.target_page] ? `Reset the ${pageLabels[step.target_page]}` : "Reset the dashboard";
        default:
            return sub;
    }
};

// PER-STEP TOGGLES support (pure, so the card stays thin + this is unit-tested). Given the plan steps and
// the set of user-DESELECTED indices:
//  - requiredNavIndices: which navigate steps are REQUIRED (a later still-selected step runs on their
//    destination page, so dropping the navigation would orphan it). The card locks those checkboxes on.
//  - selectedPlanSteps: the steps that will actually run — the selected ones, with any required navigation
//    force-kept — always a COHERENT sequence (every kept destination step is still reachable).
export const requiredNavIndices = (steps, deselected = new Set()) => {
    const req = new Set();
    (steps || []).forEach((step, i) => {
        if (!step || step.capability !== "navigate") return;
        for (let j = i + 1; j < steps.length; j++) {
            const s = steps[j];
            if (!deselected.has(j) && s && s.capability !== "navigate" && s.target_page === step.target_page) {
                req.add(i);
                break;
            }
        }
    });
    return req;
};

export const selectedPlanSteps = (steps, deselected = new Set()) => {
    const required = requiredNavIndices(steps, deselected);
    return (steps || []).filter((_step, i) => !deselected.has(i) || required.has(i));
};

const clarify = (base, message, options = []) => ({
    ...base,
    plan: true,
    needsClarification: true,
    steps: [],
    options,
    message
});

// interpretResponse = { request_id, session_id, intent_kind: "multi_task", plan: Plan };
// ctx = { utterance, navTargets, page }. Validation grounds the plan against THIS mount's reality.
export const buildPlanProposal = (interpretResponse, ctx = {}) => {
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance
    };
    const plan = (interpretResponse && interpretResponse.plan) || {};
    const rawSteps = plan.steps || [];

    // Model asked to clarify (no concrete actions identifiable).
    if (plan.needs_clarification || rawSteps.length === 0) {
        const c = plan.clarification;
        const q = (c && c.question) || plan.assistant_message || "What would you like me to do, step by step?";
        const opts = (c && c.options) || [];
        return clarify(base, opts.length ? `${q} (${opts.join(" / ")})` : q, opts);
    }

    if (rawSteps.length > MAX_PLAN_STEPS) {
        return clarify(base, `That's ${rawSteps.length} steps — I can run up to ${MAX_PLAN_STEPS} at once. Could you split the request?`);
    }

    // The pages THIS user can actually be on / reach (permission-scoped). A plan step may only target them.
    const manifest = buildPageManifest({ page: ctx.page, navTargets: ctx.navTargets });
    const reachable = new Set(manifest.map(p => p.page));
    const pageLabels = {};
    manifest.forEach(p => {
        pageLabels[p.page] = p.label;
    });

    const steps = [];
    for (const s of rawSteps) {
        if (!s || !KNOWN_CAPABILITIES.has(s.capability) || !(s.sub_utterance || "").trim()) {
            return clarify(base, "Part of that plan isn't something I can do — could you rephrase?");
        }
        if (!reachable.has(s.target_page)) {
            return clarify(base, `That plan needs the “${s.target_page}” page, which I can't open for you here.`);
        }
        steps.push({
            capability: s.capability,
            sub_utterance: s.sub_utterance.trim(),
            target_page: s.target_page,
            label: describePlanStep(s, pageLabels)
        });
    }

    return {
        ...base,
        plan: true,
        needsClarification: false,
        planId: base.requestId || `plan-${Date.now()}`,
        steps,
        options: [],
        message: plan.assistant_message || `Here's my plan (${steps.length} steps) — run it?`
    };
};
